import React, { useCallback, useEffect, useState } from 'react';
import { HTTPResponse } from '@/class/HTTPResponse';
import { StoreTweet } from '@/class/StoreTweet';
import { Weather } from '@/class/Weather';
import { fetchData } from '../../lib/api';
import AdminLayout from "../layout/AdminLayout"
import { useAuthContext } from '../../context/AuthContext';
import AdminItemList from './item/AdminItemList';
import { adminCardAccent, adminMutedText, adminSectionTitle, adminStatCard, adminStatLabel, adminStatValue } from '../layout/adminStyles';

const getTodayTweet = (tweets: StoreTweet[]): StoreTweet | null => {
  const today = new Date().toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' });

  return (
    tweets.find((tweet) => {
      if (!tweet.created_at) {
        return false;
      }

      const tweetDate = new Date(tweet.created_at).toLocaleDateString('ja-JP', {
        timeZone: 'Asia/Tokyo',
      });

      return tweetDate === today;
    }) ?? null
  );
};

const formatTweetTime = (value?: string): string | null => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleTimeString('ja-JP', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Tokyo',
  });
};

const getTimeGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour >= 4 && hour < 12) {
    return 'おはよう';
  }
  if (hour >= 12 && hour < 18) {
    return 'こんにちは';
  }
  return 'こんばんは';
};

const AdminIndexPage = () => {
  const [weather, setWeather] = useState<Weather>();
  const [todayTweet, setTodayTweet] = useState<StoreTweet | null>(null);
  const [isTweetLoading, setIsTweetLoading] = useState<boolean>(true);
  const [yesterdayPageView, setYesterdayPageView] = useState<number | null>(null);
  const { store } = useAuthContext();

  const loadYesterdayPageView = useCallback(async () => {
    if (!store?.id) {
      setYesterdayPageView(null);
      return;
    }

    try {
      const res: HTTPResponse = await fetchData(`store/${store.id}/daily-page-view/yesterday`);
      setYesterdayPageView(typeof res.data?.view_count === 'number' ? res.data.view_count : 0);
    } catch (err: unknown) {
      console.error(err);
      setYesterdayPageView(null);
    }
  }, [store?.id]);

  const loadTodayTweet = useCallback(async () => {
    if (!store?.id) {
      setTodayTweet(null);
      setIsTweetLoading(false);
      return;
    }

    setIsTweetLoading(true);
    try {
      const res: HTTPResponse = await fetchData(`store/${store.id}/tweet`);
      const tweets = Array.isArray(res.data) ? (res.data as StoreTweet[]) : [];
      setTodayTweet(getTodayTweet(tweets));
    } catch (err: unknown) {
      console.error(err);
      setTodayTweet(null);
    } finally {
      setIsTweetLoading(false);
    }
  }, [store?.id]);

  useEffect(() => {
    loadWeather();
  }, []);

  useEffect(() => {
    loadTodayTweet();
  }, [loadTodayTweet]);

  useEffect(() => {
    loadYesterdayPageView();
  }, [loadYesterdayPageView]);

  const loadWeather = async () => {
    if (!store?.weather_code) {
      return;
    }

    const cacheKey = `weather:${store.weather_code}`;

    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      setWeather(JSON.parse(cached));
    }

    try {
      const res: HTTPResponse = await fetchData(`weather?weather_code=${store.weather_code}`);
      setWeather(res.data);
      sessionStorage.setItem(cacheKey, JSON.stringify(res.data));
    } catch (err: any) {
      console.error(err.message);
    }
  }

  return (
    <AdminLayout>
      <div className={adminCardAccent}>
        <p className="mb-4 text-xl font-medium text-stone-800">
          <span className="bg-gradient-to-r from-teal-600 to-teal-500 bg-clip-text text-transparent">
            {getTimeGreeting()}
          </span>
          <span className="text-stone-700">、今日もよろしくお願いします</span>
        </p>

        {yesterdayPageView !== null && (
          <div className={`${adminStatCard} mb-4 flex items-end justify-between gap-3`}>
            <div>
              <p className={adminStatLabel}>昨日見られた回数</p>
              <p className={adminStatValue}>{yesterdayPageView.toLocaleString('ja-JP')}</p>
            </div>
            <span className="rounded-full bg-teal-500/10 px-3 py-1 text-xs font-medium text-teal-700">
              アクセス
            </span>
          </div>
        )}

        {weather && (
        <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-teal-50/60 rounded-2xl border border-teal-100/80">
          <div className="flex items-baseline gap-2">
            <span className="text-sm text-stone-500">今日の天気</span>
            <span className="text-lg font-medium text-stone-800">{weather.telop}</span>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm text-stone-600">
            <span className="text-stone-500">降水確率</span>
            <span>0-6時 {weather.chance_of_rain?.T00_06}</span>
            <span>6-12時 {weather.chance_of_rain?.T06_12}</span>
            <span>12-18時 {weather.chance_of_rain?.T12_18}</span>
            <span>18-24時 {weather.chance_of_rain?.T18_24}</span>
          </div>
        </div>
        )}

        <div className="rounded-2xl border border-stone-200/90 bg-gradient-to-br from-stone-50/80 to-white p-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className={`${adminSectionTitle} mb-2`}>本日のつぶやき</p>
          </div>

          {isTweetLoading ? (
            <p className={`${adminMutedText} text-sm`}>読み込み中...</p>
          ) : todayTweet ? (
            <>
              <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-stone-800">
                {todayTweet.body}
              </p>
              {formatTweetTime(todayTweet.created_at) && (
                <p className="mt-2 text-xs text-stone-500">
                  {formatTweetTime(todayTweet.created_at)}
                </p>
              )}
            </>
          ) : (
            <p className={`${adminMutedText} text-sm`}>
              本日のつぶやきはまだありません。
            </p>
          )}
        </div>
      </div>

      <div className="mt-6">
        <AdminItemList />
      </div>
    </AdminLayout>
  );
};

export default AdminIndexPage;
